import assetManifest from '../../assets/images/mulnyang-bulnyang/asset-manifest.json';
import { MULNYANG_ASSET_MAP } from './mulnyangAssetMap';

const randomInt = (max) => Math.floor(Math.random() * max);

const pickRandomItem = (items) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return items[randomInt(items.length)];
};

const pickWeightedType = (difficultyKey) => {
  // to
  const weightedTypes =
    difficultyKey === 'easy' || difficultyKey === 'normal'
      ? [
          { type: 'cat', weight: 40 },
          { type: 'letter', weight: 40 },
          { type: 'number', weight: 20 },
        ]
      : [
          { type: 'cat', weight: 50 },
          { type: 'letter', weight: 50 },
        ];

  const total = weightedTypes.reduce((acc, item) => acc + item.weight, 0);
  let pivot = Math.random() * total;
  for (const item of weightedTypes) {
    if (pivot < item.weight) return item.type;
    pivot -= item.weight;
  }
  return weightedTypes[weightedTypes.length - 1]?.type ?? 'letter';
};

export const DIFFICULTIES = [
  { key: 'easy', label: '쉬움', snackCost: 1, clearPoint: 2, grid: '4×4', rows: 4, cols: 4, limitSeconds: 8 },
  { key: 'normal', label: '보통', snackCost: 2, clearPoint: 4, grid: '5×5', rows: 5, cols: 5, limitSeconds: 10 },
  { key: 'hard', label: '어려움', snackCost: 3, clearPoint: 18, grid: '6×7', rows: 7, cols: 6, limitSeconds: 10 },
  { key: 'hell', label: '지옥', snackCost: 5, clearPoint: 40, grid: '6×9', rows: 9, cols: 6, limitSeconds: 10 },
];

const ASSET_KEYS = new Set(Object.keys(MULNYANG_ASSET_MAP));

const normalizeAssetPath = (relativePath) => {
  if (!relativePath) return '';
  const clean = relativePath.replace(/^\.\//, '');
  const candidates = [clean, clean.normalize('NFC'), clean.normalize('NFD')];
  return candidates.find((candidate) => ASSET_KEYS.has(candidate)) ?? '';
};

let resolvedAssets = null;
let boardPools = null;
const OPEN_LETTER_SET_MAX_NO = 20;

const ensureAssetsLoaded = () => {
  if (resolvedAssets && boardPools) return;

  const resolveMulnyangAsset = (relativePath) => {
    const normalizedPath = normalizeAssetPath(relativePath);
    return normalizedPath ? MULNYANG_ASSET_MAP[normalizedPath] : '';
  };

  const hasAsset = (relativePath) => Boolean(resolveMulnyangAsset(relativePath));

  const TILE_PALETTES = Array.from({ length: 7 }, (_, index) => {
    const no = String(index + 1).padStart(2, '0');
    const baseImage = `tiles/tile_${no}_base.png`;
    const dimImage = `tiles/tile_${no}_dim.png`;
    if (!hasAsset(baseImage) || !hasAsset(dimImage)) return null;
    return { id: no, baseImage, dimImage };
  }).filter(Boolean);

  const LETTER_ITEMS = assetManifest.letters
    .filter((item) => Number(item.no) >= 1 && Number(item.no) <= OPEN_LETTER_SET_MAX_NO)
    .filter((item) => item.assetReady)
    .filter((item) => hasAsset(item.baseImage) && hasAsset(item.oddImage));

  const NUMBER_ITEMS = assetManifest.numbers
    .filter((item) => item.assetReady)
    .filter((item) => hasAsset(item.baseImage) && hasAsset(item.oddImage));

  const CAT_ITEMS = assetManifest.cats.filter(
    (item) => hasAsset(item.baseImage) && hasAsset(item.easyNormalOddImage) && hasAsset(item.hardHellOddImage),
  );

  resolvedAssets = { TILE_PALETTES, LETTER_ITEMS, NUMBER_ITEMS, CAT_ITEMS, resolveMulnyangAsset };

  const easyNormalCatPool = CAT_ITEMS.map((item) => ({
    kind: 'cat',
    id: item.id,
    baseImage: item.baseImage,
    oddImage: item.easyNormalOddImage,
  }));
  const hardHellCatPool = CAT_ITEMS.map((item) => ({
    kind: 'cat',
    id: item.id,
    baseImage: item.baseImage,
    oddImage: item.hardHellOddImage,
  }));

  boardPools = {
    easy: {
      letters: LETTER_ITEMS.filter((item) => item.mode === '쉬움/보통'),
      numbers: NUMBER_ITEMS.filter((item) => item.digits <= 2 && item.mode.includes('쉬움')),
      cats: easyNormalCatPool,
    },
    normal: {
      letters: LETTER_ITEMS.filter((item) => item.mode === '쉬움/보통'),
      numbers: NUMBER_ITEMS.filter((item) => item.digits >= 2 && item.mode.includes('보통')),
      cats: easyNormalCatPool,
    },
    hard: {
      letters: LETTER_ITEMS.filter((item) => item.mode === '어려움/지옥'),
      numbers: [],
      cats: hardHellCatPool,
    },
    hell: {
      letters: LETTER_ITEMS.filter((item) => item.mode === '어려움/지옥'),
      numbers: [],
      cats: hardHellCatPool,
    },
  };
};

export const getBoardAssetStats = () => {
  ensureAssetsLoaded();
  return {
    letters: resolvedAssets.LETTER_ITEMS.length,
    numbers: resolvedAssets.NUMBER_ITEMS.length,
    cats: resolvedAssets.CAT_ITEMS.length,
    tiles: resolvedAssets.TILE_PALETTES.length,
  };
};

export const buildBoard = (difficulty) => {
  ensureAssetsLoaded();
  const pools = boardPools[difficulty.key] ?? boardPools.easy;
  const letterPool = pools.letters;
  const numberPool = pools.numbers;
  const catPool = pools.cats;

  const contentType = pickWeightedType(difficulty.key);
  let selected = null;

  if (contentType === 'letter') selected = pickRandomItem(letterPool);
  else if (contentType === 'number') selected = pickRandomItem(numberPool);
  else selected = pickRandomItem(catPool);

  if (!selected) {
    selected = pickRandomItem(letterPool) ?? pickRandomItem(numberPool) ?? pickRandomItem(catPool);
  }
  if (!selected) return null;

  const totalCount = difficulty.rows * difficulty.cols;
  const oddIndex = randomInt(totalCount);
  const tilePalette = pickRandomItem(resolvedAssets.TILE_PALETTES) ?? resolvedAssets.TILE_PALETTES[0];
  if (!tilePalette) return null;
  const tileBaseSrc = resolvedAssets.resolveMulnyangAsset(tilePalette.baseImage);
  const tileDimSrc = resolvedAssets.resolveMulnyangAsset(tilePalette.dimImage);
  const overlayBaseSrc = resolvedAssets.resolveMulnyangAsset(selected.baseImage);
  const overlayOddSrc = resolvedAssets.resolveMulnyangAsset(selected.oddImage);
  if (!tileBaseSrc || !tileDimSrc || !overlayBaseSrc || !overlayOddSrc) return null;

  const startWithBase = Math.random() >= 0.5;

  const cells = Array.from({ length: totalCount }, (_, index) => {
    const row = Math.floor(index / difficulty.cols);
    const col = index % difficulty.cols;
    const useBase = startWithBase ? (row + col) % 2 === 0 : (row + col) % 2 !== 0;
    return {
      id: `${difficulty.key}-${index}`,
      tileSrc: useBase ? tileBaseSrc : tileDimSrc,
      overlaySrc: index === oddIndex ? overlayOddSrc : overlayBaseSrc,
      isOdd: index === oddIndex,
    };
  });

  return {
    boardId: `${difficulty.key}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    difficultyKey: difficulty.key,
    difficultyLabel: difficulty.label,
    clearPoint: difficulty.clearPoint,
    grid: difficulty.grid,
    limitSeconds: difficulty.limitSeconds,
    rows: difficulty.rows,
    cols: difficulty.cols,
    contentType,
    cells,
  };
};
